const CANVAS_SIZE = 480
const PADDING = 40
const CAPTION_FONT_SIZE = 28
const CAPTION_MIN_FONT_SIZE = 16
const CAPTION_FONT_FAMILY = 'Euclid Square'
const ELLIPSIS = '…'

type CaptionContextT = Pick<CanvasRenderingContext2D, 'font' | 'measureText'>

const captionFont = (size: number) => `${size}px ${CAPTION_FONT_FAMILY}`

/**
 * Fits a caption into `maxWidth`: shrinks the font one step at a time down to
 * CAPTION_MIN_FONT_SIZE, then truncates with an ellipsis if it still doesn't
 * fit. Long captions (e.g. facility names) otherwise run off the canvas edge.
 * Leaves `context.font` set to the chosen size.
 */
export const fitCaption = (
  context: CaptionContextT,
  text: string,
  maxWidth: number
): { text: string; fontSize: number } => {
  const fits = (value: string) => context.measureText(value).width <= maxWidth

  for (
    let fontSize = CAPTION_FONT_SIZE;
    fontSize >= CAPTION_MIN_FONT_SIZE;
    fontSize -= 1
  ) {
    context.font = captionFont(fontSize)
    if (fits(text)) return { text, fontSize }
  }

  let truncated = text
  while (truncated && !fits(`${truncated}${ELLIPSIS}`)) {
    truncated = truncated.slice(0, -1)
  }
  return {
    text: `${truncated.trimEnd()}${ELLIPSIS}`,
    fontSize: CAPTION_MIN_FONT_SIZE,
  }
}

// Downloads Canvas element as a PNG
export const downloadQRasPNG = (
  name: string,
  title: string,
  elementId: string
) => {
  const oldCanvas = document.getElementById(elementId) as HTMLCanvasElement

  const newCanvas = document.createElement('canvas')
  const context = newCanvas.getContext('2d')

  newCanvas.width = CANVAS_SIZE
  newCanvas.height = CANVAS_SIZE

  // create white background
  context.fillStyle = 'white'
  context.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)

  // create style for text inside canvas
  context.fillStyle = '#5C6C7A'
  // copy oldCanvas to newCanvas
  context.drawImage(
    oldCanvas,
    PADDING,
    PADDING,
    CANVAS_SIZE - PADDING * 2,
    CANVAS_SIZE - PADDING * 2
  )
  const caption = fitCaption(context, title, CANVAS_SIZE - PADDING * 2)
  context.fillText(caption.text, PADDING, CANVAS_SIZE - 5)

  const image = newCanvas.toDataURL('image/png')
  const anchor = document.createElement('a')
  anchor.href = image
  anchor.download = `${name}-qr-code.png`
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}
